import { WorldMap } from "./WorldMap";

type RowState = "pending" | "ok" | "nxdomain" | "timeout" | "error" | "servfail";
type Row = { resolver: string; state: RowState; latencyMs?: number };

type Props = { rows: Row[] };

/**
 * Console-style globe-and-stats sidebar: the existing animated WorldMap
 * sits at the top, with a 2x2 grid below summarizing the current lookup
 * (counts + a few latency stats).
 */
export function MapPanel({ rows }: Props) {
  const okCount = rows.filter((r) => r.state === "ok" || r.state === "nxdomain").length;
  const failCount = rows.filter((r) => r.state === "timeout" || r.state === "error" || r.state === "servfail").length;
  const pendingCount = rows.filter((r) => r.state === "pending").length;

  const okLatencies = rows
    .filter((r) => (r.state === "ok" || r.state === "nxdomain") && typeof r.latencyMs === "number")
    .map((r) => r.latencyMs as number)
    .sort((a, b) => a - b);

  const p50 = pickPercentile(okLatencies, 0.50);
  const p95 = pickPercentile(okLatencies, 0.95);

  return (
    <aside className="map-panel">
      <div className="panel-title">RESOLVER MAP <b>· 14 sites</b></div>
      <WorldMap rows={rows} />

      <div className="side-stats" aria-label="Lookup summary">
        <Stat label="OK" value={okCount} tone={okCount > 0 ? "ok" : undefined} />
        <Stat label="FAIL" value={failCount} tone={failCount > 0 ? "fail" : undefined} />
        <Stat label="p50" value={p50 == null ? "—" : p50} unit={p50 == null ? undefined : "ms"} />
        <Stat label="p95" value={p95 == null ? "—" : p95} unit={p95 == null ? undefined : "ms"} />
      </div>

      {pendingCount > 0 ? (
        <p className="map-pending" aria-live="polite">
          <span className="map-pending-dot" /> {pendingCount} querying…
        </p>
      ) : null}
    </aside>
  );
}

function Stat({
  label, value, unit, tone,
}: {
  label: string;
  value: number | string;
  unit?: string;
  tone?: "ok" | "fail";
}) {
  return (
    <div className="cell">
      <div className="k">{label}</div>
      <div className={`v${tone ? ` v-${tone}` : ""}`}>
        {value}
        {unit ? <span className="unit">{unit}</span> : null}
      </div>
    </div>
  );
}

function pickPercentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  const idx = Math.min(sorted.length - 1, Math.floor(p * sorted.length));
  return sorted[idx];
}
